import { useEffect, useRef, useState, useCallback } from 'react';
import { HubConnectionState } from '@microsoft/signalr';
import type { HubConnection } from '@microsoft/signalr';
import { getGameState, setPlayerName as setPlayerNameApi } from '../services/gameStateService';
import { warpFleet, recallFleet } from '../services/fleetService';
import { warpShip, recallShip, purchaseUpgrade, buildShip } from '../services/shipService';
import { changeMiningZone } from '../services/miningZoneService';
import { debugLog } from '../utils/debugLogger';
import { useSignalR } from '../context/SignalRContext';
import { isNewBrowserSession, generateSessionId } from '../utils/sessionStorage';
import { onActivity } from '../utils/activityMonitor';
import { GAME_STATE_IMPORTED_EVENT } from '../constants/events';
import { errorService } from '../services/errorService';
import type { ShipDto } from '../types/ship-types';

function getOrCreatePlayerId() {
  let playerId = localStorage.getItem('playerId');
  if (!playerId) {
    playerId = crypto.randomUUID();
    localStorage.setItem('playerId', playerId);
  }
  return playerId;
}
function getOrCreatePlayerName() {
  const playerName = localStorage.getItem('playerName');
  if (!playerName) return null;
  return playerName;
}

// Show the "welcome back" summary only after a real break, not a quick tab switch.
const DEFAULT_ABSENCE_THRESHOLD_MS = 5 * 60 * 1000;

// Allow for development override from localStorage (using seconds for easier testing)
const getAbsenceThreshold = () => {
  const overrideSeconds = localStorage.getItem('debugAbsenceThresholdSeconds');
  if (overrideSeconds && !isNaN(parseInt(overrideSeconds))) {
    const seconds = parseInt(overrideSeconds);
    debugLog(`Using debug absence threshold: ${seconds} seconds`);
    return seconds * 1000;
  }
  return DEFAULT_ABSENCE_THRESHOLD_MS;
};

const ABSENCE_THRESHOLD_MS = getAbsenceThreshold();
const LOCAL_STORAGE_LAST_ACTIVITY_KEY = 'lastActivityTime';

export function useGameState() {
  const { connection } = useSignalR();
  const [playerId, setPlayerId] = useState(getOrCreatePlayerId());
  const [playerName, setPlayerName] = useState(getOrCreatePlayerName());
  // Only show modal if no player name is found in localStorage
  const [showModal, setShowModal] = useState(!playerName);
  const [loading, setLoading] = useState(true);
  const [gameState, setGameState] = useState<any>(null);
  const [debugMode, setDebugMode] = useState(false);
  const [pollInterval, setPollInterval] = useState(2000);
  const pollTimer = useRef<number | null>(null);

  // State for tracking absence
  const [showReturnModal, setShowReturnModal] = useState(false);
  const [absenceData, setAbsenceData] = useState<{
    absenceTime: number;
    oreMined: number;
    tripsCompleted: number;
  }>({
    absenceTime: 0,
    oreMined: 0,
    tripsCompleted: 0
  });

  // Reference to track the last time the user was active within the app
  const userActiveTimestampRef = useRef<number>(Date.now());

  // Reference to store previous ore and trips count
  const previousStateRef = useRef<{
    basicOre: number;
    totalTrips: number;
    lastUpdateTime: number;
  }>({
    basicOre: 0,
    totalTrips: 0,
    lastUpdateTime: Date.now()
  });

  // Setup user activity tracking
  useEffect(() => {
    // Update our local reference whenever the user is active
    const unsubscribe = onActivity((timestamp) => {
      userActiveTimestampRef.current = timestamp;

      // Also update localStorage to indicate activity
      localStorage.setItem(LOCAL_STORAGE_LAST_ACTIVITY_KEY, timestamp.toString());
    });

    return () => {
      unsubscribe();
    };
  }, []);

  // Helper to get connection safely
  const getHubConnection = async () => {
    if (!connection) {
      debugLog('[SignalR] No hub connection instance available');
      return null;
    }

    if (connection.state === HubConnectionState.Disconnected) {
      debugLog('[SignalR] Hub is disconnected, attempting restart...');
      try {
        await connection.start();
      } catch (err) {
        debugLog('[SignalR] Failed to start hub connection', err);
        return null;
      }
    }

    if (connection.state === HubConnectionState.Connecting || connection.state === HubConnectionState.Reconnecting) {
      debugLog('[SignalR] Hub is still connecting, try again shortly');
      return null;
    }

    return connection;
  };

  // Show modal and set player name
  const handlePlayerNameSubmit = async (name: string) => {
    const hub = await getHubConnection();
    if (!hub) {
      console.error('Cannot set player name: No SignalR connection');
      return;
    }

    setPlayerName(name);
    localStorage.setItem('playerName', name);
    setShowModal(false);
    // Also update the backend
    try {
      await setPlayerNameApi(hub, playerId, name);
    } catch (e) {
      // Optionally handle error (e.g., show notification)
      console.error('Failed to set player name on backend:', e);
    }
  };

  // Handle return from absence
  const handleReturnModalClose = () => {
    debugLog("Return modal closed by user");
    setShowReturnModal(false);

    // Update last activity time to now
    const now = Date.now();
    localStorage.setItem(LOCAL_STORAGE_LAST_ACTIVITY_KEY, now.toString());

    // Also update previous state reference timestamp to avoid double-counting
    previousStateRef.current.lastUpdateTime = now;
  };

  // Load game state
  const loadGameState = useCallback(async (showLoader = false, checkAbsence = true, overridePlayerId?: string) => {
    if (!connection) {
      return;
    }

    if (showLoader) setLoading(true);
    try {
      const effectivePlayerId = overridePlayerId ?? playerId;
      const data = await getGameState(connection, effectivePlayerId);
      if (overridePlayerId && overridePlayerId !== playerId) {
        setPlayerId(overridePlayerId);
      }
      setGameState(data);
      // Save the loaded game state to localStorage for export/import
      localStorage.setItem('gameState', JSON.stringify(data));
      localStorage.setItem('playerId', effectivePlayerId);

      // Process absence detection
      const now = Date.now();
      const lastActivityTime = parseInt(localStorage.getItem(LOCAL_STORAGE_LAST_ACTIVITY_KEY) || now.toString());
      const absenceTime = now - lastActivityTime;

      // Calculate total trips based on fleet data
      const currentTotalTrips = Array.isArray(data?.fleet)
        ? data.fleet.reduce((sum: number, ship: any) => sum + (ship.tripCount || 0), 0)
        : 0;

      debugLog(`Absence check: Time away: ${absenceTime}ms, Threshold: ${ABSENCE_THRESHOLD_MS}ms`);

      // If absence is greater than threshold and we're checking for absence, show return modal with stats
      if (checkAbsence && absenceTime >= ABSENCE_THRESHOLD_MS) {
        debugLog("Absence detected, showing welcome back panel");

        const oreMined = Math.max(0, data?.resources?.basicOre - previousStateRef.current.basicOre);
        const tripsCompleted = Math.max(0, currentTotalTrips - previousStateRef.current.totalTrips);

        setAbsenceData({
          absenceTime,
          oreMined,
          tripsCompleted
        });

        setShowReturnModal(true);
      }

      // Update previous state reference
      previousStateRef.current = {
        basicOre: data?.resources?.basicOre || 0,
        totalTrips: currentTotalTrips,
        lastUpdateTime: now
      };

      // Only update the last activity time if we're not checking for absence or if absence wasn't detected
      if (!checkAbsence || absenceTime < ABSENCE_THRESHOLD_MS) {
        localStorage.setItem(LOCAL_STORAGE_LAST_ACTIVITY_KEY, now.toString());
      }

    } catch (e) {
      console.error("Failed to load game state via SignalR:", e);
    } finally {
      setLoading(false);
    }
  }, [playerId, connection]);

  // Polling
  useEffect(() => {
    if (pollTimer.current) clearInterval(pollTimer.current);
    pollTimer.current = window.setInterval(() => loadGameState(false, false), pollInterval);
    return () => { if (pollTimer.current) clearInterval(pollTimer.current); };
  }, [pollInterval, playerId, loadGameState]);

  // Initial load
  useEffect(() => {
    if (playerName) {
      // Check for absence on initial load
      const now = Date.now();
      const lastTime = parseInt(localStorage.getItem(LOCAL_STORAGE_LAST_ACTIVITY_KEY) || now.toString());
      const absenceTime = now - lastTime;

      // Check if this is a new browser session (browser was closed and reopened)
      const isNewSession = isNewBrowserSession();

      debugLog(`Initial load check: Time away: ${absenceTime}ms, Threshold: ${ABSENCE_THRESHOLD_MS}ms, New session: ${isNewSession}`);

      // If this is a new browser session and absence is significant, we definitely want to show the welcome back
      const shouldCheckAbsence = isNewSession
        ? absenceTime >= ABSENCE_THRESHOLD_MS  // For new sessions, check if absence meets threshold
        : absenceTime >= ABSENCE_THRESHOLD_MS; // For tab switches, same logic but separated for clarity

      // Always pass true for showLoader, and determine checkAbsence based on time away and session
      loadGameState(true, shouldCheckAbsence);
    } else {
      // Generate a new session ID when the app initializes without a player name
      // (This ensures we have a session ID ready when they enter their name)
      generateSessionId();
    }
  }, [playerName, playerId, loadGameState]);

  // Set up visibility change detection to track when user leaves/returns to page
  useEffect(() => {
    // Record when the user is leaving the page (tab hidden)
    const handleVisibilityChangeHidden = () => {
      if (document.visibilityState === 'hidden') {
        const timestamp = Date.now();
        debugLog(`Page hidden, recording timestamp: ${timestamp}`);

        // Store the timestamp both in localStorage (persists across browser close)
        // and in sessionStorage (only persists within tab session)
        localStorage.setItem(LOCAL_STORAGE_LAST_ACTIVITY_KEY, timestamp.toString());
        sessionStorage.setItem('tabHiddenTime', timestamp.toString());
      }
    };

    // Check for absence when user returns to the page (tab visible again)
    const handleVisibilityChangeVisible = () => {
      if (document.visibilityState === 'visible') {
        // Only process if we have a player name (user is logged in)
        if (!playerName) return;

        debugLog("Page visible again, checking for absence");

        const now = Date.now();
        const tabHiddenTime = parseInt(sessionStorage.getItem('tabHiddenTime') || '0');
        const lastActivityTime = parseInt(localStorage.getItem(LOCAL_STORAGE_LAST_ACTIVITY_KEY) || now.toString());

        // Calculate time away - use tab hidden time if available, otherwise use last activity time
        const tabSwitchAbsenceTime = tabHiddenTime > 0 ? now - tabHiddenTime : 0;
        const absenceTime = Math.max(tabSwitchAbsenceTime, now - lastActivityTime);

        debugLog(`Tab return: Tab switch time: ${tabSwitchAbsenceTime}ms, Global time away: ${now - lastActivityTime}ms, Threshold: ${ABSENCE_THRESHOLD_MS}ms`);

        // Clear tab hidden time since we're back
        sessionStorage.removeItem('tabHiddenTime');

        if (absenceTime >= ABSENCE_THRESHOLD_MS) {
          // Check absence and get latest game state
          loadGameState(false, true);
        } else {
          // Even if we haven't been away long enough, update the timestamp
          localStorage.setItem(LOCAL_STORAGE_LAST_ACTIVITY_KEY, now.toString());
        }
      }
    };

    // Handle page reload/initial load absence detection
    const checkInitialAbsence = () => {
      const now = Date.now();
      const lastTime = parseInt(localStorage.getItem(LOCAL_STORAGE_LAST_ACTIVITY_KEY) || now.toString());
      const absenceTime = now - lastTime;

      debugLog(`Initial load: Time away: ${absenceTime}ms, Threshold: ${ABSENCE_THRESHOLD_MS}ms`);

      if (absenceTime >= ABSENCE_THRESHOLD_MS) {
        // Don't update timestamp on initial check to allow loadGameState to handle it
        return true;
      }
      return false;
    };

    // Initialize last activity time if not set
    if (!localStorage.getItem(LOCAL_STORAGE_LAST_ACTIVITY_KEY)) {
      localStorage.setItem(LOCAL_STORAGE_LAST_ACTIVITY_KEY, Date.now().toString());
    } else {
      // Check for absence on initial load/reload
      checkInitialAbsence();
    }

    // Add visibility change listeners
    document.addEventListener('visibilitychange', handleVisibilityChangeHidden);
    document.addEventListener('visibilitychange', handleVisibilityChangeVisible);

    // When the page is about to unload, update the timestamp
    window.addEventListener('beforeunload', () => {
      debugLog("Page unloading, recording timestamp");
      localStorage.setItem(LOCAL_STORAGE_LAST_ACTIVITY_KEY, Date.now().toString());
    });

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChangeHidden);
      document.removeEventListener('visibilitychange', handleVisibilityChangeVisible);
    };
  }, [loadGameState, playerName]);

  // Track tab/browser close
  useEffect(() => {
    const handleBeforeUnload = () => {
      debugLog("Before unload: Recording last activity timestamp");
      localStorage.setItem(LOCAL_STORAGE_LAST_ACTIVITY_KEY, Date.now().toString());
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  // Runs a hub action, refreshes state, and surfaces server rejections (e.g. "Not enough ore")
  // as a visible error instead of an unhandled promise rejection.
  const runAction = async (label: string, action: (hub: HubConnection) => Promise<unknown>) => {
    const hub = await getHubConnection();
    if (!hub) return;
    setLoading(true);
    try {
      await action(hub);
      await loadGameState(false, false);
    } catch (e) {
      // SignalR wraps server rejections as "...on the server. HubException: <reason>"; keep the reason.
      const raw = e instanceof Error ? e.message : String(e);
      const message = raw.replace(/^.*HubException:\s*/s, '');
      errorService.addError(`${label}: ${message}`);
    } finally {
      setLoading(false);
    }
  };

  const doWarpFleet = () => runAction('Warp fleet', (hub) => warpFleet(hub, playerId));
  const doRecallFleet = () => runAction('Recall fleet', (hub) => recallFleet(hub, playerId));
  const doWarpShip = (shipId: string) => runAction('Warp ship', (hub) => warpShip(hub, playerId, shipId));
  const doRecallShip = (shipId: string) => runAction('Recall ship', (hub) => recallShip(hub, playerId, shipId));
  const doPurchaseUpgrade = (shipId: string, branchType: string | number, upgradeName: string) =>
    runAction('Upgrade', (hub) => purchaseUpgrade(hub, playerId, shipId, branchType, upgradeName));
  const doBuildShip = (type: string | number) => runAction('Build ship', (hub) => buildShip(hub, playerId, type));
  const doChangeMiningZone = (zoneId: string) => runAction('Change zone', (hub) => changeMiningZone(hub, playerId, zoneId));

  // Status bar values
  const credits = gameState?.resources?.credits ?? 0;
  const basicOre = gameState?.resources?.basicOre ?? 0;
  const fleet: ShipDto[] = gameState?.fleet || [];
  // Debug function to force show the return panel (for testing)
  const forceShowReturnPanel = useCallback(() => {
    if (!gameState) return;

    debugLog("Debug: Forcing welcome back panel to appear");

    // Use a minimum 5 minute absence time for testing
    const testAbsenceTime = Math.max(5 * 60 * 1000, ABSENCE_THRESHOLD_MS);

    setAbsenceData({
      absenceTime: testAbsenceTime,
      oreMined: Math.floor(Math.random() * 1000) + 100, // Random amount for testing
      tripsCompleted: Math.floor(Math.random() * 5) + 1  // Random trips for testing
    });

    setShowReturnModal(true);
  }, [gameState]);

  useEffect(() => {
    if (gameState && gameState.playerName) {
      // Update both the state and localStorage when we get a player name from the backend
      localStorage.setItem('playerName', gameState.playerName);
      setPlayerName(gameState.playerName);
      setShowModal(false);
      debugLog(`Using player name from saved game: ${gameState.playerName}`);
    }
  }, [gameState]);
  // Initial load effect - if we're loading the game and we find a player name in localStorage,
  // make sure we hide the modal right away (don't wait for backend response)
  useEffect(() => {
    const storedPlayerName = localStorage.getItem('playerName');
    if (storedPlayerName && !playerName) {
      setPlayerName(storedPlayerName);
      setShowModal(false);
      debugLog(`Using stored player name from localStorage: ${storedPlayerName}`);
    }
  }, [playerName]);

  useEffect(() => {
    const handleImported = () => {
      const importedId = localStorage.getItem('playerId');
      const importedName = localStorage.getItem('playerName');
      if (importedName) {
        setPlayerName(importedName);
        setShowModal(false);
      }
      if (importedId) {
        loadGameState(true, true, importedId);
      }
    };
    window.addEventListener(GAME_STATE_IMPORTED_EVENT, handleImported);
    return () => window.removeEventListener(GAME_STATE_IMPORTED_EVENT, handleImported);
  }, [loadGameState]);

  return {
    playerId,
    playerName,
    setPlayerName,
    showModal,
    setShowModal,
    loading,
    setLoading,
    gameState,
    setGameState,
    debugMode,
    setDebugMode,
    pollInterval,
    setPollInterval,
    handlePlayerNameSubmit,
    loadGameState,
    credits,
    basicOre,
    fleet,
    doWarpFleet,
    doRecallFleet,
    doWarpShip,
    doRecallShip,
    doPurchaseUpgrade,
    doBuildShip,
    doChangeMiningZone,    // Return modal related
    showReturnModal,
    setShowReturnModal,
    absenceData,
    handleReturnModalClose,
    forceShowReturnPanel
  };
}
